/**
 * Lifecycle policy — the frontend mirror of the wire contract.
 *
 * Source of truth: `backend/modules/lifecycle/schemas.py` +
 * `docs/automation-module/lifecycle-policy-contract.md`. A change lands in all three
 * artefacts in one pass or it is not a change.
 *
 * CONSTANTS ONLY: no shape assertions and no date math here. `due_at` / `at` come from
 * the server, which owns the clock; the screen only formats them. The fixtures in
 * `./fixtures.js` are for tests and pre-integration work.
 *
 * The closed sets are plain `export const NAME = [...]` arrays on purpose: the backend
 * pin test (`tests/test_lifecycle_frontend_constants_pinned.py`) reads them with a regex.
 */

export const LIFECYCLE_MOMENTS = [
  "welcome",
  "trial_reminder",
  "trial_converted",
  "dunning_first_notice",
  "dunning_follow_up",
  "suspended_non_payment",
  "subscription_cancelled",
  "service_ended",
  "plan_changed",
];

export const SCHEDULED_MOMENTS = ["trial_reminder", "dunning_follow_up"];

export const UPCOMING_MOMENTS = ["trial_reminder", "dunning_follow_up", "suspended_non_payment"];

export const SEND_OUTCOMES = ["sent", "no_transport", "failed", "skipped_disabled"];

export const PLAN_CHANGE_TYPES = ["upgraded", "downgraded", "reactivated"];

export const VIOLATION_RULES = [
  "unknown_field",
  "type",
  "min",
  "max",
  "unique",
  "max_items",
  "lt_trial_length",
  "lt_grace_days",
];

export const STRIPE_CONTROLLED = ["smart_retries", "checkout_session_ttl"];

export const LIFECYCLE_ERROR_CODES = ["POLICY_INVALID", "REAUTH_REQUIRED", "INVALID_HORIZON"];

/**
 * Display bounds for the inputs. The server re-validates every save and answers 422
 * POLICY_INVALID with `{field, rule}` violations; `GET /policy` also returns `bounds`,
 * which the screen prefers when present. These values must equal `schemas.BOUNDS`.
 */
export const LIFECYCLE_BOUNDS = {
  trial_length_days: { min: 1, max: 90 },
  reminder_lead_days: { min: 1, max: 30 },
  max_reminders: 5,
  grace_days: { min: 0, max: 30 },
  follow_up_day: { min: 1 },
  max_follow_ups: 5,
};

export const UPCOMING_DAYS_DEFAULT = 14;
export const SENDS_LIMIT_DEFAULT = 50;

/** Every on/off toggle on the screen: [dotted policy path, moment it gates]. */
export const EMAIL_TOGGLES = [
  ["welcome_email.enabled", "welcome"],
  ["trial.reminders.enabled", "trial_reminder"],
  ["trial.converted_email.enabled", "trial_converted"],
  ["dunning.first_notice.enabled", "dunning_first_notice"],
  ["dunning.follow_ups.enabled", "dunning_follow_up"],
  ["dunning.suspended_email.enabled", "suspended_non_payment"],
  ["cancellation.cancelled_email.enabled", "subscription_cancelled"],
  ["cancellation.service_ended_email.enabled", "service_ended"],
  ["plan_change_email.enabled", "plan_changed"],
];

/** API paths, relative to the axios base (`lib/api.js`, which already prefixes /api). */
export const LIFECYCLE_PATHS = {
  policy: "/super-admin/lifecycle/policy",
  upcoming: "/super-admin/lifecycle/upcoming",
  sends: "/super-admin/lifecycle/sends",
  run: "/super-admin/lifecycle/run",
  reauth: "/super-admin/gateway/reauth",
};
